"use client";

import { TRENDING_NEWS_API } from "@/lib/constants";
import { useQuery } from "@tanstack/react-query";
import Lottie from "lottie-react";
import { ChevronLeftIcon, ChevronRightIcon, TrendingUpIcon } from "lucide-react";
import { useState } from "react";
import trendingAnimation from "./trending.json";

type CmsTrendingNews = {
	title: string;
	link: string;
	region?: string;
};

async function getTrendingNewsData(): Promise<CmsTrendingNews[]> {
	try {
		const res = await fetch(TRENDING_NEWS_API, {
			next: { revalidate: 60 },
		});

		if (!res.ok) {
			console.error("Trending News API failed with status:", res.status);
			throw new Error(`Failed to fetch trending news: ${res.status}`);
		}

		const json = await res.json();

		if (!json || typeof json !== "object" || !Array.isArray(json.data)) {
			console.error("Unexpected API response format:", json);
			throw new Error("Invalid API response format");
		}
		const validData = json.data.filter(
			(item: any) => item && typeof item.title === "string" && typeof item.link === "string"
		);

		return validData;
	} catch (error) {
		console.error("Error fetching trending news:", error);
		return [];
	}
}

export const TrendingNewsCard = () => {
	const [currentRegionIndex, setCurrentRegionIndex] = useState(0);
	const {
		isLoading,
		isError,
		data: trendingNews = [],
	} = useQuery({
		queryKey: ["trendingNews"],
		queryFn: getTrendingNewsData,
		refetchOnWindowFocus: false,
	});
	const safeTrendingNews = Array.isArray(trendingNews) ? trendingNews : [];

	if (isLoading) {
		return (
			<div className="relative rounded-lg bg-white">
				<div className="flex items-center justify-center py-24">
					<p className="text-sm text-gray-600">Loading trending news...</p>
				</div>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="relative rounded-lg bg-white">
				<div className="flex items-center justify-center py-24">
					<p className="text-sm text-red-500">Error fetching trending news</p>
				</div>
			</div>
		);
	}
	const regions = safeTrendingNews.map((news) => news.region || "Global");
	const uniqueRegions = Array.from(new Set(regions)).sort((a, b) => {
		if (a === "Global") return -1;
		if (b === "Global") return 1;
		return a.localeCompare(b);
	});
	const currentRegion = uniqueRegions[currentRegionIndex] || "Global";
	const currentRegionNews = safeTrendingNews.filter(
		(news) => (news.region || "Global") === currentRegion
	);

	const handleShowPreviousRegion = () => {
		if (currentRegionIndex > 0) {
			setCurrentRegionIndex((prev) => prev - 1);
		} else {
			setCurrentRegionIndex(uniqueRegions.length - 1);
		}
	};

	const handleShowNextRegion = () => {
		if (currentRegionIndex < uniqueRegions.length - 1) {
			setCurrentRegionIndex((prev) => prev + 1);
		} else {
			setCurrentRegionIndex(0);
		}
	};

	const handleNewsClick = (newsItem: CmsTrendingNews) => {
		window.rudderanalytics?.track("click", {
			target_url: newsItem.link,
			link_text: newsItem.title,
		});
		if (newsItem.link) {
			window.open(newsItem.link, "_blank", "noopener,noreferrer");
		}
	};

	return (
		<div className="relative rounded-lg bg-white">
			<div className="flex justify-between p-4">
				<div className="flex items-center gap-1">
					<Lottie animationData={trendingAnimation} loop={true} className="h-6 w-6" />
					<h4 className="font-semibold uppercase text-gray-700">Trending</h4>
				</div>

				{uniqueRegions.length > 1 && (
					<div className="flex items-center gap-1">
						<button onClick={handleShowPreviousRegion}>
							<ChevronLeftIcon className="h-4 w-4" />
						</button>
						<span className="whitespace-nowrap text-center text-xs">{currentRegion}</span>
						<button onClick={handleShowNextRegion}>
							<ChevronRightIcon className="h-4 w-4" />
						</button>
					</div>
				)}
			</div>

			<div className="trending-card flex h-36 flex-col overflow-y-scroll">
				{currentRegionNews.length > 0 ? (
					currentRegionNews.map((newsItem, index) => (
						<div
							key={`${newsItem.link}-${index}`}
							className="flex items-center gap-4 border-t border-t-gray-100 px-4 py-3"
						>
							<TrendingUpIcon className="h-6 w-6 text-brand-primary" />
							<div
								onClick={() => handleNewsClick(newsItem)}
								className="customized-scrollbar trending-text flex-1 cursor-pointer text-xs text-gray-500 transition-colors duration-200 hover:text-blue-600"
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										handleNewsClick(newsItem);
									}
								}}
							>
								{newsItem.title}
							</div>
						</div>
					))
				) : (
					<div className="mx-4 rounded-md border border-brand-primary/20 bg-brand-primary/10 p-4 text-sm font-medium text-brand-primary">
						No news to show
					</div>
				)}
			</div>
		</div>
	);
};
